import jobUpdateListener, {JobUpdate, JobUpdateSubscriber} from "@/services/JobUpdateListener";
import ProgressSmoother from "@/utils/ProgressSmoother";
import {measureElement} from "@tanstack/react-virtual";

export interface ProgressListenerConfig {
    jobId: string;
    progressSmoother?: ProgressSmoother;
    updatesPerSecond?: number;
    waitForRunning?: boolean;
    requestInitialProgress?: boolean;
    onProgress: (progress: number) => void;
    onComplete?: (result?: any) => void;
    onError: (error: Error) => void;
}

export interface ProgressListener {
    disconnect: () => void;
}

export function createProgressListener(config: ProgressListenerConfig): ProgressListener {
    const progressSmoother = config.progressSmoother;
    let cancelUpdates: (() => void) | null = null;
    let isInitialized = !config.waitForRunning;

    const disconnect = () => {
        cancelUpdates?.();
        jobUpdateListener.unsubscribe(config.jobId, messageSubscriber);
    };

    const messageSubscriber: JobUpdateSubscriber = {
        onMessage(message: JobUpdate) {

            if (message.type !== "progress" && message.type !== "status") {
                return
            }

            if (message.status) {
                const normalized = message.status.toUpperCase();
                switch (normalized) {
                    case 'RUNNING':
                        isInitialized = true;
                        break;
                    case 'FAILED':
                        const errorMessage = 'error' in message ? message.error : null;
                        config.onError(new Error(errorMessage || "Job failed, please try again later."));
                        disconnect();
                        break;
                    case 'SUCCEEDED':
                        isInitialized = true;
                        config.onProgress(progressSmoother?.maxValue ?? 1);
                        config.onComplete?.(message);
                        disconnect();
                        break;
                    default:
                        break;
                }
            }

            if (message.type === "progress") {
                if (!isInitialized) {
                    if (message.status) {
                        const normalized = message.status.toUpperCase();
                        if (normalized === 'RUNNING') {
                            isInitialized = true;
                        }
                    }
                    if (!isInitialized) {
                        return;
                    }
                }

                if (progressSmoother) {
                    progressSmoother.setValue(message.progress * progressSmoother.maxValue);
                } else {
                    config.onProgress(message.progress);
                }

            }
        },

        onError(error: Error) {
            config.onError(error);
        }
    };

    jobUpdateListener.subscribe(config.jobId, messageSubscriber).then(() => {
        if (config.requestInitialProgress) {
            jobUpdateListener.requestProgress(config.jobId);
        }

        if (progressSmoother) {
            const frequency = 1000 / (config.updatesPerSecond || 2);
            cancelUpdates = progressSmoother.subscribeToUpdates(
                (value) => {
                    if (isInitialized) {
                        config.onProgress(value);
                    }
                },
                frequency
            );
        }
    }).catch(config.onError);
    return {
        disconnect
    };
}
